// Despair's clock (§26 F9, §31 T): the stamp and the grace, in turns of the round.
//
// **A stamp is a turn slot, `round * SLOT_SIDES + turn`** — which turn of which
// round the diagnosis landed on. Never `turnCounter` (ticks, two or more a
// turn) and never `ply` (a turn holds several or none): either shrank the
// one-turn grace to a tick. Slots are monotonic in (round, turn) and
// `SLOT_SIDES` is the side cap, so armies landing mid-run cannot reorder them.
//
// **Grace is the in-danger side's own turns, completed.** Counting rounds alone
// is lopsided: side 0 diagnosed during side 1's turn would reach the next
// round before it had played one. One reading, three readers — the reducer's
// `surrender` gate, `despairStep`, the banner.

import type { GameState, RunState } from "../commands";
import { CLASS_MONARCH, pieceColor } from "../board";
import { DESPAIR_NONE, pieceClassAt, type Position } from "../position";
import { despairTurnsFor } from "./mode";

/**
 * Is `side` in the shape the grace period is for (§31 K6, design "Normal")?
 * Only Monarchs left, or no Monarch at all — a faction in either has one of
 * its own turns (`despairTurnsFor`) to forge its way out, then loses. A fact
 * about the board, so it rides beside `isDepressed`'s judgement rather than
 * through it. False for a side with nothing on the board — that one is gone.
 */
export function graceShape(pos: Position, side: number): boolean {
  let monarchs = 0;
  let others = 0;
  for (const [sq, piece] of pos.board) {
    if (pieceColor(piece) !== side) continue;
    if (pieceClassAt(pos, sq) === CLASS_MONARCH) monarchs++;
    else others++;
    if (monarchs > 0 && others > 0) return false;
  }
  return monarchs + others > 0;
}

/**
 * Does a side that has lost its last king play on under the grace period
 * rather than lose on the spot (§31 K6)? In a run with a grace (Normal); never
 * on Hard (`despairTurns` 0) and never outside a run, where losing the king is
 * the end as it always was.
 */
export const kinglessGrace = (run: RunState | undefined | null): boolean =>
  !!run && despairTurnsFor(run) > 0;

/** Sides are three bits; a slot leaves room for all eight. */
export const SLOT_SIDES = 8;

/** The turn slot the clock stands on: what `set-despair` stamps. */
export const despairSlot = (pos: Position): number => pos.round * SLOT_SIDES + pos.turn;

/** `side`'s own turns that began at or after slot `at` and have ended by now. */
export function ownTurnsSince(pos: Position, side: number, at: number): number {
  const now = despairSlot(pos);
  const first = Math.ceil((at - side) / SLOT_SIDES);
  const last = Math.floor((now - 1 - side) / SLOT_SIDES);
  return Math.max(0, last - first + 1);
}

/** Turns of grace `side` still has, or null when it is not despairing. 0 = may surrender. */
export function despairTurnsLeft(state: GameState, side: number): number | null {
  const at = state.position.despair[side] ?? DESPAIR_NONE;
  if (at < 0) return null;
  return Math.max(0, despairTurnsFor(state.run) - ownTurnsSince(state.position, side, at));
}

/**
 * Is the clock at the start of a turn — nothing moved, nothing ticked? The
 * only moment despair is judged (driver and `playRun`), so a turn's ticks
 * never re-run it mid-turn.
 */
export const atTurnStart = (pos: Position): boolean =>
  pos.turnMoves === 0 && pos.turnTicks === 0 && pos.turnForged === 0;
